/**
 * Resolución de configuración por entorno del panel admin. Equivalente web del
 * `AppConfig` de admin_app (Flutter `--dart-define` + `.env`).
 *
 * `resolveAdminConfig` es PURA (recibe un record y devuelve la config) para
 * poder testearla; `adminConfig` la resuelve sobre `import.meta.env` en runtime.
 *
 * La config Firebase web es pública (ya en admin_app/lib/firebase_options.dart):
 * hay defaults para que la app arranque sin `.env`. Solo se sobreescribe por VITE_*.
 */

export type AdminEnvironment = 'dev' | 'staging' | 'prod';

/** Región de las Cloud Functions (callables Maps/comandos). Compartida con rider. */
export const FUNCTIONS_REGION = 'europe-west1';

export interface FirebaseWebConfig {
  apiKey: string;
  appId: string;
  messagingSenderId: string;
  projectId: string;
  authDomain: string;
  databaseURL: string;
  storageBucket: string;
  measurementId: string;
}

export interface AdminConfig {
  environment: AdminEnvironment;
  apiBaseUrl: string;
  firebase: FirebaseWebConfig;
  recaptchaSiteKey: string | null;
  sentryDsn: string | null;
  mapsApiKey: string | null;
  useFirebaseEmulators: boolean;
  /** Interruptor técnico de despliegue; no sustituye los permisos del usuario. */
  webMcpEnabled: boolean;
  webPushVapidKey: string | null;
  /** Gate público de lectura de la superficie fiscal canónica. */
  verifactuReadOnlyEnabled: boolean;
  /** Gate de mutaciones, efectivo únicamente cuando la lectura está habilitada. */
  verifactuMutationsEnabled: boolean;
}

/** Defaults públicos (proyecto `flutter-uber-clone-198ad`, superficie web admin). */
const FIREBASE_DEFAULTS: FirebaseWebConfig = {
  apiKey: 'AIzaSyD8yhSyNobj62GynqnzpZxeiOGJ_osWAVY',
  appId: '1:918930752152:web:ada0b8c99f910edf9046ed',
  messagingSenderId: '918930752152',
  projectId: 'flutter-uber-clone-198ad',
  authDomain: 'admin.ibercar.eu',
  databaseURL: 'https://flutter-uber-clone-198ad-default-rtdb.europe-west1.firebasedatabase.app',
  storageBucket: 'flutter-uber-clone-198ad.appspot.com',
  measurementId: 'G-CVHG9JYBLC',
};

const API_BASE_URL_BY_ENV: Record<AdminEnvironment, string> = {
  dev: 'https://dev-api.ibercar.com',
  staging: 'https://nest-api-staging-53aadxoxgq-ew.a.run.app',
  prod: 'https://api.ibercar.eu',
};

type EnvRecord = Record<string, string | undefined>;

function pick(env: EnvRecord, key: string, fallback: string): string {
  const v = env[key];
  return v !== undefined && v !== '' ? v : fallback;
}

function pickOptional(env: EnvRecord, key: string): string | null {
  const v = env[key];
  return v !== undefined && v !== '' ? v : null;
}

/** Resuelve la config a partir de un record de variables (puro, testeable). */
export function resolveAdminConfig(env: EnvRecord): AdminConfig {
  const rawEnv = pick(env, 'VITE_ADMIN_ENV', 'prod');
  const environment: AdminEnvironment =
    rawEnv === 'dev' || rawEnv === 'staging' || rawEnv === 'prod' ? rawEnv : 'prod';
  const verifactuReadOnlyEnabled =
    pick(env, 'VITE_USE_VERIFACTU_INVOICES', 'false') === 'true';
  const verifactuMutationsRequested =
    pick(env, 'VITE_ENABLE_VERIFACTU_MUTATIONS', 'false') === 'true';

  return {
    environment,
    apiBaseUrl: pick(env, 'VITE_API_BASE_URL', API_BASE_URL_BY_ENV[environment]),
    firebase: {
      apiKey: pick(env, 'VITE_FIREBASE_API_KEY', FIREBASE_DEFAULTS.apiKey),
      appId: pick(env, 'VITE_FIREBASE_APP_ID', FIREBASE_DEFAULTS.appId),
      messagingSenderId: pick(env, 'VITE_FIREBASE_MESSAGING_SENDER_ID', FIREBASE_DEFAULTS.messagingSenderId),
      projectId: pick(env, 'VITE_FIREBASE_PROJECT_ID', FIREBASE_DEFAULTS.projectId),
      authDomain: pick(env, 'VITE_FIREBASE_AUTH_DOMAIN', FIREBASE_DEFAULTS.authDomain),
      databaseURL: pick(env, 'VITE_FIREBASE_DATABASE_URL', FIREBASE_DEFAULTS.databaseURL),
      storageBucket: pick(env, 'VITE_FIREBASE_STORAGE_BUCKET', FIREBASE_DEFAULTS.storageBucket),
      measurementId: pick(env, 'VITE_FIREBASE_MEASUREMENT_ID', FIREBASE_DEFAULTS.measurementId),
    },
    recaptchaSiteKey: pickOptional(env, 'VITE_RECAPTCHA_SITE_KEY'),
    sentryDsn: pickOptional(env, 'VITE_SENTRY_DSN'),
    mapsApiKey: pickOptional(env, 'VITE_MAPS_API_KEY'),
    useFirebaseEmulators: pick(env, 'VITE_USE_FIREBASE_EMULATORS', 'false') === 'true',
    webMcpEnabled: pick(env, 'VITE_WEBMCP_ENABLED', 'false') === 'true',
    webPushVapidKey: pickOptional(env, 'VITE_WEB_PUSH_VAPID_KEY'),
    verifactuReadOnlyEnabled,
    verifactuMutationsEnabled:
      verifactuReadOnlyEnabled && verifactuMutationsRequested,
  };
}

/** Config resuelta en runtime (Vite inyecta `import.meta.env` en build). */
export const adminConfig: AdminConfig = resolveAdminConfig(
  import.meta.env as unknown as EnvRecord,
);
